import { Divider } from 'src/design/atoms/Divider';
import { Group } from 'src/design/atoms/Group';
import { SimpleGrid } from 'src/design/atoms/SimpleGrid';
import { Stack } from 'src/design/atoms/Stack';
import { Title } from 'src/design/atoms/Title';
import { TextInput } from 'src/design/atoms/TextInput';
import { SecondaryButton } from 'src/design/atoms/SecondaryButton';
import { PaperStack } from 'src/design/atoms/PaperStack';
import { useFormik } from 'formik';
import React from 'react';
import {
  JobDto,
  NotificationCompositionRequest,
  ResourceAttachmentDto,
  SubmittalDto,
  SubmittalRequestDto,
  UserDto,
} from 'src/api/FiretrolApiClient';
import {
  usePreviewSubmittalPdf,
  useRotateSubmittalExternalAccess,
  useSaveSubmittal,
} from 'src/api/queries/submittals/useSubmittalMutations';
import { useUploadTempAttachment } from 'src/api/queries/workflowModules/useAttachmentMutations';
import { FileDrop } from 'src/design/organisms/FileDrop/FileDrop';
import { applyAttachmentChange } from 'src/design/organisms/attachments/attachmentEdits';
import { WorkflowRecordSection } from 'src/app/workflows/WorkflowRecordSection';
import { workflowRecordFieldSchema } from 'src/app/workflows/workflowRecordFieldSchema';
import { useWorkflowPdfPreview } from 'src/app/workflows/useWorkflowPdfPreview';
import { useWorkflowAttachmentEdits } from 'src/app/workflows/useWorkflowAttachmentEdits';
import { useWorkflowState } from 'src/app/workflows/WorkflowButtons/useWorkflowState';
import { isStepEditable } from 'src/app/workflows/isStepEditable';
import { useRevokeExternalLinks } from 'src/app/workflows/useRevokeExternalLinks';
import { SubmittalDocumentPicker } from 'src/routes/Jobs/Submittals/components/SubmittalDocumentPicker';
import { useNotificationComposer } from 'src/hooks/useNotificationComposer';
import { WorkflowFormFooter } from 'src/app/workflows/WorkflowFormFooter';
import { useTransitionComposer } from 'src/app/workflows/useTransitionComposer';
import { useWorkflowFormTransitions } from 'src/app/workflows/useWorkflowFormTransitions';
import { isTerminalStatus } from 'src/app/workflows/workflowLifecycle';
import { useAuth } from 'src/hooks/useAuth';
import { useWorkflowSubmission } from 'src/hooks/useWorkflowSubmission';
import { FIELD_LIMITS, getErrorMessage, logger } from 'src/utils';
import * as Yup from 'yup';
import { IContact } from 'src/api/models/Organization';
import { contactDtoToIContact } from 'src/app/workflows/contactDtoToIContact';
import { useWorkflowFormValidation } from 'src/app/workflows/useWorkflowFormValidation';
import { useResolveAttachmentUrl } from 'src/api/queries/attachments/useAttachmentMutations';

interface FormTarget {
  name: string;
  value: string;
}

const SUBMITTAL_FIELD_LABELS: Record<string, string> = {
  title: 'Title',
  assigneeUserId: 'Assignee',
  company: 'Company Name',
  intentOfReview: 'Intent of Review',
  milestonePhaseItemId: 'Linked Milestone Item',
  externalSubmittalNumber: 'Submittal # (External)',
};

const validationSchema = Yup.object().shape({
  ...workflowRecordFieldSchema,
  externalSubmittalNumber: Yup.string()
    .nullable()
    .max(
      FIELD_LIMITS.singleLine,
      `Submittal # (External) must be ${FIELD_LIMITS.singleLine} characters or fewer`,
    ),
});

/** Which slice of the form to render; omit to render the full single-column form (create flow). */
export type SubmittalFormSection =
  | 'details'
  | 'attachments'
  | 'notifications'
  | 'history';

export interface SubmittalFormValues extends SubmittalDto {
  filesToAdd: ResourceAttachmentDto[];
  filesToRemove: ResourceAttachmentDto[];
}

interface ISubmittalFormProps {
  initialValues: SubmittalDto;
  selectedJob: JobDto;
  onSaved?: (submittal: SubmittalDto) => void;
  onCreated?: (submittal: SubmittalDto) => void;
  /** In ViewSubmittal the form is split across tabs; this selects the visible slice. Omit for the single-column create flow. */
  activeSection?: SubmittalFormSection;
  jobUsers?: UserDto[];
  /** Content for non-form tabs (notifications, history) rendered above the persistent footer. */
  children?: React.ReactNode;
}

export const SubmittalForm: React.FC<ISubmittalFormProps> = ({
  onSaved,
  onCreated,
  initialValues,
  selectedJob,
  activeSection,
  jobUsers = [],
  children,
}) => {
  const { user, isAdmin } = useAuth();

  const formik = useFormik<SubmittalFormValues>({
    initialValues: {
      ...initialValues,
      filesToAdd: [],
      filesToRemove: [],
    },
    validationSchema,
    validateOnMount: true,
    onSubmit: async () => {},
  });
  const { values, setFieldValue } = formik;

  const { currentStep } = useWorkflowState(
    values.workflow ?? null,
    values.stepIndex ?? -1,
  );

  const [submittedToContact, setSubmittedToContact] =
    React.useState<IContact | null>(
      contactDtoToIContact(
        initialValues.submittedToContact,
        initialValues.submittedToContactId,
      ),
    );

  const composer = useNotificationComposer(
    'submittal',
    selectedJob.id,
    values.id ?? '',
  );
  const transition = useTransitionComposer(composer.loadPreview);

  const saveSubmittal = useSaveSubmittal();
  const previewSubmittalPdf = usePreviewSubmittalPdf();
  const rotateExternalAccess = useRotateSubmittalExternalAccess();
  const uploadTempAttachment = useUploadTempAttachment();
  const downloadAttachment = useResolveAttachmentUrl();

  const pdfPreview = useWorkflowPdfPreview({
    fetchPdf: () =>
      previewSubmittalPdf.mutateAsync({
        jobId: selectedJob.id,
        id: values.id ?? '',
      }),
    logLabel: 'Failed to load submittal PDF preview',
  });

  const revoker = useRevokeExternalLinks<SubmittalDto>({
    rotate: () =>
      rotateExternalAccess.mutateAsync({
        jobId: selectedJob.id,
        id: values.id ?? '',
      }),
    onRevoked: (dto) => {
      formik.resetForm({
        values: {
          ...values,
          attachments: dto.attachments ?? values.attachments,
        },
      });
      onSaved?.(dto);
    },
    logLabel: 'Failed to revoke submittal external links',
  });

  const buildRequest = (
    overrides: Partial<SubmittalRequestDto> = {},
  ): SubmittalRequestDto => ({
    id: values.id || undefined,
    title: values.title,
    type: values.type,
    intentOfReview: values.intentOfReview,
    externalSubmittalNumber: values.externalSubmittalNumber,
    company: values.company,
    status: values.status,
    stepIndex: values.stepIndex,
    workflowId: values.workflowId,
    assigneeUserId: values.assigneeUserId,
    reporterUserId: values.reporterUserId,
    milestonePhaseItemId: values.milestonePhaseItemId,
    submittedToContactId: values.submittedToContactId,
    filesToAdd: values.filesToAdd?.map((f) => ({ id: f.id })),
    filesToRemove: values.filesToRemove?.map((f) => ({ id: f.id })),
    saveOnly: false,
    notification: undefined,
    ...overrides,
  });

  const {
    submitWorkflow,
    voidWorkflow,
    saveOnly,
    error,
    fieldErrors,
    isSubmitting,
  } = useWorkflowSubmission<SubmittalFormValues, SubmittalRequestDto>(
    values,
    selectedJob.id,
    {
      mutations: { save: saveSubmittal },
      toRequest: (_vals, overrides) =>
        buildRequest({
          status: overrides?.status,
          stepIndex: overrides?.stepIndex,
          saveOnly: overrides?.saveOnly ?? false,
          notification: overrides?.notification,
        }),
      entityName: 'Submittal',
      toast: { getEntityNumber: (s) => s.submittalNumber ?? '' },
    },
    {
      onCreated,
      onSaved,
      setValues: (dto) => {
        formik.resetForm({
          values: {
            ...values,
            ...dto,
            attachments: dto.attachments ?? values.attachments,
            filesToAdd: [],
            filesToRemove: [],
            workflow: dto.workflow ?? values.workflow,
          },
        });
      },
    },
  );

  const setValue = ({ target }: { target: FormTarget }) => {
    setFieldValue(target.name, target.value);
  };

  const { passesValidation, fieldError } =
    useWorkflowFormValidation<SubmittalFormValues>(
      formik,
      fieldErrors,
      SUBMITTAL_FIELD_LABELS,
    );

  const { onSubmitClick, submitTransition } =
    useWorkflowFormTransitions<SubmittalFormValues>({
      values,
      submitWorkflow,
      transition,
      entityLabel: 'submittal',
    });

  const handleSubmitClick = async (status: string, nextStepIndex: number) => {
    if (!(await passesValidation())) return;
    await onSubmitClick(status, nextStepIndex);
  };

  const handleSaveOnly = async () => {
    if (!(await passesValidation())) return;
    await saveOnly();
  };

  const { saveComment, addDocument } = useWorkflowAttachmentEdits(
    formik.setValues,
    selectedJob.id,
  );

  const isDisabled = !isStepEditable({
    isAdmin,
    currentStep,
    status: values.status,
  });
  const reporter = values.reporter
    ? values.reporter.fullName
    : `${user?.firstName ?? ''} ${user?.lastName ?? ''}`;
  const customerNumber = selectedJob.customerNumber?.trim() ?? '';
  const isTerminal = isTerminalStatus(values.status);

  const showDetails = !activeSection || activeSection === 'details';
  const showAttachments = !activeSection || activeSection === 'attachments';

  const attachmentsSection = (
    <PaperStack>
      <Group justify="space-between">
        <Title order={4}>Document</Title>
        {values.id && (values.attachments ?? []).some((f) => f.externalUrl) && (
          <SecondaryButton onClick={revoker.openModal}>
            Revoke links
          </SecondaryButton>
        )}
      </Group>
      <SubmittalDocumentPicker
        jobId={selectedJob.id}
        onAttached={addDocument}
      />
      <FileDrop
        disabled={currentStep?.isLastStep || values.status === 'Voided'}
        files={values.attachments ?? []}
        onChange={(files, change) => {
          const edits = applyAttachmentChange(
            { added: values.filesToAdd, removed: values.filesToRemove },
            change,
          );
          formik.setValues((v) => ({
            ...v,
            attachments: files as ResourceAttachmentDto[],
            filesToAdd: edits.added as ResourceAttachmentDto[],
            filesToRemove: edits.removed as ResourceAttachmentDto[],
          }));
        }}
        onCommentSave={saveComment}
        onCopyLink={revoker.copyLink}
        uploadFile={(file) =>
          uploadTempAttachment.mutateAsync({ typeId: 'submittals', file })
        }
        downloadFile={(id) => downloadAttachment.mutateAsync(id)}
      />
    </PaperStack>
  );

  return (
    <Stack gap="md" pb="md">
      {showDetails && (
        <>
          <PaperStack>
            <Group justify="space-between">
              <Title order={4}>Details</Title>
              {values.id && (
                <SecondaryButton onClick={pdfPreview.open}>
                  Preview PDF
                </SecondaryButton>
              )}
            </Group>
            <SimpleGrid cols={{ base: 1, md: 2 }}>
              <Stack gap="sm">
                <TextInput label="Job" value={selectedJob.name} disabled />
                <TextInput
                  label="Client"
                  value={selectedJob.customerName}
                  disabled
                />
                <TextInput label="Reporter" value={reporter} disabled />
              </Stack>
              <Stack gap="sm">
                <TextInput
                  label="Number"
                  name="submittalNumber"
                  value={values.submittalNumber ?? ''}
                  disabled
                  placeholder="Auto Generated"
                />
                <TextInput
                  label="Submittal # (External)"
                  name="externalSubmittalNumber"
                  disabled={isDisabled}
                  value={
                    isDisabled && !values.externalSubmittalNumber
                      ? 'N/A'
                      : values.externalSubmittalNumber ?? ''
                  }
                  onChange={setValue}
                  onBlur={formik.handleBlur}
                  maxLength={FIELD_LIMITS.singleLine}
                  error={fieldError('externalSubmittalNumber')}
                  placeholder="ex: SUBMITTAL-XXXX"
                />
                <TextInput
                  label="Status"
                  name="status"
                  value={values.status ?? ''}
                  disabled
                  placeholder="N/A"
                />
              </Stack>
            </SimpleGrid>
            {isAdmin && (
              <TextInput
                label="Assigned Workflow"
                description="(Admin Only)"
                name="workflow"
                value={values.workflow?.name}
                disabled
              />
            )}
          </PaperStack>
          <WorkflowRecordSection
            sectionTitle="Submittal"
            title={values.title}
            assigneeUserId={values.assigneeUserId}
            company={values.company}
            intentOfReview={values.intentOfReview}
            milestonePhaseItemId={values.milestonePhaseItemId}
            disabled={isDisabled}
            jobId={selectedJob.id}
            customerNumber={customerNumber}
            customerName={selectedJob.customerName ?? ''}
            submittedToContact={submittedToContact}
            onFieldChange={setFieldValue}
            onSubmittedToChange={setSubmittedToContact}
            fieldError={fieldError}
          />
        </>
      )}

      {showAttachments && (
        <>
          {showDetails && <Divider />}
          {attachmentsSection}
        </>
      )}

      {children}

      <WorkflowFormFooter
        dirty={formik.dirty}
        showButtons={!isTerminal}
        error={error}
        onSubmitClick={handleSubmitClick}
        workflow={values.workflow ?? null}
        onSaveOnlyClick={handleSaveOnly}
        onVoidClick={voidWorkflow}
        stepIndex={values.stepIndex ?? -1}
        composerOpen={transition.opened}
        onComposerClose={transition.cancel}
        preview={composer.preview}
        jobUsers={jobUsers}
        customerNumber={customerNumber}
        composerSubmitting={isSubmitting}
        composerTitle={`${
          transition.pending?.stepLabel ?? 'Submittal'
        } — notification`}
        attachPdfLabel="Attach Submittal PDF"
        onSend={submitTransition}
        onSkip={submitTransition}
        onReloadPreview={composer.reloadPreview}
        previewOpen={pdfPreview.opened}
        onPreviewClose={pdfPreview.close}
        pdfTitle="Submittal PDF"
        previewUrl={pdfPreview.url}
        previewLoading={pdfPreview.loading}
        revokeOpen={revoker.modalOpen}
        onRevokeClose={revoker.closeModal}
        onRevokeConfirm={revoker.revoke}
        revoking={revoker.revoking}
        revokeEntityNoun="Submittal"
        revokeItemsNoun="attachments"
      />
    </Stack>
  );
};
